/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import './Navigation.css';

interface NavItem {
  path: string;
  label: string;
  icon: JSX.Element;
}
interface NavigationProps {
  isAdmin: boolean;
  onLogoutClick: () => void; 
}

const Navigation: React.FC<NavigationProps> = ({ isAdmin, onLogoutClick }) => {
 
  const location = useLocation();
const navItems: NavItem[] = [
  {
    path: '/account',
    label: 'Dashboard',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
      </svg>
    )
  },
  {
    path: '/dashboard',
    label: 'My Sales',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    )
  },
  {
    path: '/products',
    label: 'My Products',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    )
  },
  // Edit Content link hidden for now - leaving isAdmin ? [...] : [] in
  // place (just forced to the empty branch) so it's a one-line revert
  // to bring back.
 ...(false && isAdmin ? [{
    path: '/edit-content',
    label: 'Edit Content',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
   )
  }] : []),
  ...(isAdmin ? [{
    path: '/admin-tools',
    label: 'Admin Tools',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    )
  }] : [])
];
  const isActive = (path: string): boolean => location.pathname === path;

  // The bottom tab bar sits right at thumb height, so a logout button
  // there is one accidental tap away - unlike the desktop header button,
  // which has no such risk. window.confirm() renders as a native dialog
  // inside Capacitor's webview on iOS/Android, so no extra plugin is
  // needed just for this.
  const handleBottomNavLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogoutClick();
    }
  };

  const logoutIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );

  return (
    <>
      {/* Desktop Navigation ALLOWING BOTH USERS FOR NOW*/}

      {(isAdmin || !isAdmin) &&
      <nav className="desktop-nav">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
          </Link>
        ))}
      </nav>
        }
      {/* Mobile Bottom Tab Bar - portaled directly onto <body> so no
          ancestor's overflow/position/stacking-context can ever affect
          this fixed-position UI. */}
      {(isAdmin || !isAdmin) && createPortal(
        <nav className="bottom-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`bottom-nav-link ${isActive(item.path) ? 'active' : ''}`}
            >
              <span className="bottom-nav-icon">{item.icon}</span>
              <span className="bottom-nav-label">{item.label}</span>
            </Link>
          ))}
          <button className="bottom-nav-link bottom-nav-logout" onClick={handleBottomNavLogout}>
            <span className="bottom-nav-icon">{logoutIcon}</span>
            <span className="bottom-nav-label">Logout</span>
          </button>
        </nav>,
        document.body
      )}
    </>
  );
};

export default Navigation;